import { useEffect, useMemo, useState } from "react";

import { Search } from "../../../../../components/design-system";
import { COPY } from "../../copy";
import {
  formatCustomerCurrency,
  formatCustomerDate,
  getAcquiredCustomerRows,
  getAttributedOrder,
  hasContinuedAttribution,
  type AcquiredCustomerRow,
  type CustomerType,
} from "../../customersData";
import { INSIGHTS_STORE_TYPES } from "../../insightsMockData";
import type { InsightsCustomerSegment } from "../../types";
import {
  useCardPaddingClass,
  useIsCompact,
  useTableRowHeightClass,
} from "../CardDensity";
import { InsightsFilterMenu } from "../InsightsFilterMenu";
import { InsightsSectionTitle } from "../InsightsSectionTitle";
import { NotApplicableCell } from "../NotApplicableCell";
import { DottedLabel } from "../overview/DottedLabel";
import { HeaderTooltip } from "../overview/HeaderTooltip";
import { TablePagination } from "../TablePagination";

const PAGE_SIZE = 8;

/** Sentinel for the unfiltered state, so the filter's value is always a string. */
const ALL_STORE_TYPES = "all";

/**
 * The same store types the store-type cards above group by, so a reader can
 * pick one there and then find those retailers by name here.
 *
 * The unfiltered option collapses to "Store types: All" on the trigger, so the
 * chip stays as short as the tab's other filters while the menu still spells the
 * option out.
 */
const STORE_TYPE_FILTER_OPTIONS = [
  { value: ALL_STORE_TYPES, label: "All store types", triggerLabel: "All" },
  ...INSIGHTS_STORE_TYPES.map((storeType) => ({
    value: storeType,
    label: storeType,
  })),
];

/**
 * The tab-level filter speaks in segments; the rows carry a customer type. A
 * segment of "all-customers" maps to no row constraint at all.
 */
const SEGMENT_ROW_TYPE: Record<InsightsCustomerSegment, CustomerType | null> = {
  "all-customers": null,
  "new-customers": "New",
  "returning-customers": "Returning",
};

/**
 * Singular, unlike the filter menus: a row is one retailer's one conversion, so
 * it names the single ad that drove it rather than the campaign type.
 */
const AD_TYPE_LABEL: Record<CustomerType, string> = {
  New: "New customer ad",
  Returning: "Reorder ad",
};

const AD_TYPE_TOOLTIP = COPY.customers.table.adType;

/** The column definitions this table shares with the cohort drilldowns. */
const STORE_TYPE_TOOLTIP = COPY.shared.columns.storeType;

const ORDER_DATE_TOOLTIP = COPY.shared.columns.orderDate;

const LIFETIME_SALES_TOOLTIP = COPY.shared.columns.lifetimeSales;

const LIFETIME_ORDERS_TOOLTIP = COPY.shared.columns.lifetimeOrders;

/**
 * Same glyph the sortable tables use for their active column, but this table has
 * no sort controls, so it sits in a plain span rather than a button: it reports
 * the order the rows arrive in without implying the header can be clicked.
 */
function DefaultSortIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="shrink-0 text-[#333]"
      aria-hidden
    >
      <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
    </svg>
  );
}

/**
 * The underline lives on the value rather than the cell, so the em dash standing
 * in for a missing number doesn't get dressed up as a link.
 */
function LifetimeCell({
  value,
  underline = false,
}: {
  value: string | null;
  underline?: boolean;
}) {
  return (
    <span
      className={`${value == null ? "text-[#757575]" : "text-[#333]"} ${
        underline && value != null ? "underline" : ""
      }`}
    >
      {value ?? "—"}
    </span>
  );
}

export function CustomersAcquiredTable({
  customerSegment,
}: {
  customerSegment: InsightsCustomerSegment;
}) {
  const [query, setQuery] = useState("");
  const [storeType, setStoreType] = useState<string>(ALL_STORE_TYPES);
  const [page, setPage] = useState(1);
  const cardPaddingClass = useCardPaddingClass();
  const rowHeightClass = useTableRowHeightClass();
  // V3 right-aligns the money and count columns so the figures line up on
  // their last digit, the way the Overview month table does.
  const figureAlignClass = useIsCompact() ? "text-right" : "";

  // Sorted on the date the Order date column actually shows, which is a
  // different field per ad type, so the sort arrow can't point at a column
  // ordered by something the reader can't see.
  const allRows = useMemo(
    () =>
      [...getAcquiredCustomerRows()].sort((left, right) =>
        getAttributedOrder(right).date.localeCompare(
          getAttributedOrder(left).date
        )
      ),
    []
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const requiredType = SEGMENT_ROW_TYPE[customerSegment];
    return allRows.filter((row) => {
      if (
        normalizedQuery &&
        !row.storeName.toLowerCase().includes(normalizedQuery)
      ) {
        return false;
      }
      if (requiredType != null && row.customerType !== requiredType) {
        return false;
      }
      if (storeType !== ALL_STORE_TYPES && row.storeType !== storeType) {
        return false;
      }
      return true;
    });
  }, [allRows, query, customerSegment, storeType]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetPage = () => setPage(1);

  // The segment filter lives on the tab now, so a change arrives as a new prop
  // rather than through a local handler that could reset the page itself.
  useEffect(() => {
    setPage(1);
  }, [customerSegment]);

  return (
    <div
      className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      {/* Who's in the list and how it's sorted — the table's own framing, kept
          off the column headers because it describes all of them at once. */}
      <h3 className="text-sm leading-5 font-medium text-[#333]">
        <InsightsSectionTitle tooltip={COPY.customers.table.headingTooltip}>
          {COPY.customers.table.heading}
        </InsightsSectionTitle>
      </h3>
      <div className="mt-4 mb-4 flex flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
        <div className="w-full min-[733px]:w-[348px]">
          <Search
            placeholder="Search by store name"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              resetPage();
            }}
            onClear={() => {
              setQuery("");
              resetPage();
            }}
          />
        </div>
        {/* Beside the search rather than up with the tab filters: it narrows
            this list only, and the Store type column it filters is right below
            it. */}
        <div className="w-full min-[733px]:w-auto">
          <InsightsFilterMenu
            name="customers-table-store-type"
            labelPrefix="Store types"
            menuTitle="Filter by store type"
            value={storeType}
            options={STORE_TYPE_FILTER_OPTIONS}
            onChange={(value) => {
              setStoreType(value);
              resetPage();
            }}
            clearValue={ALL_STORE_TYPES}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        {/* Wide enough for nine columns before the numbers start colliding.
            "New customer ad" sets the ad type column where a bare "New"
            didn't. */}
        <table className="w-full min-w-[1280px] table-fixed text-left text-sm">
          {/* Pinned widths, so paging doesn't reshuffle the columns: a
              `table-auto` table sizes each one to the widest cell on the
              current page, and these cells hold names and figures of very
              different lengths.

              The figure columns are sized to their own headers, which are the
              longest thing in them and must not wrap. What they need comes out
              of store name, store type, and location, whose values clip with an
              ellipsis and stay readable on hover. */}
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "11.5%" }} />
            <col style={{ width: "10%" }} />
            {/* "New customer ad" doesn't wrap, so this column is sized to it
                rather than to its own header. */}
            <col style={{ width: "11.5%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "11.5%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-[#dfe0e1]">
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Store name
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={STORE_TYPE_TOOLTIP}>
                  <DottedLabel>Store type</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Location
              </th>
              {/* Right of the descriptive columns, ahead of the dates and
                  dollars, matching the cohort table's order. */}
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={AD_TYPE_TOOLTIP}>
                  <DottedLabel>Ad type</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Ad click date
              </th>
              {/* The sort glyph sits outside the dotted label, so the underline
                  marks the words that carry the tooltip and not the icon. */}
              <th
                className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]"
                aria-sort="descending"
              >
                <span className="inline-flex items-center gap-1">
                  <HeaderTooltip tooltip={ORDER_DATE_TOOLTIP}>
                    <DottedLabel>Order date</DottedLabel>
                  </HeaderTooltip>
                  <DefaultSortIcon />
                </span>
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
              >
                Order amount
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
              >
                <HeaderTooltip tooltip={LIFETIME_SALES_TOOLTIP}>
                  <DottedLabel>Lifetime sales</DottedLabel>
                </HeaderTooltip>
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
              >
                <HeaderTooltip tooltip={LIFETIME_ORDERS_TOOLTIP}>
                  <DottedLabel>Lifetime orders</DottedLabel>
                </HeaderTooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row: AcquiredCustomerRow) => {
              const order = getAttributedOrder(row);
              // Lifetime belongs to the customers new customer ads acquired. A
              // reorder ad's credit stops at the reorder, so both totals read as
              // not-applicable rather than crediting the ad with a history it
              // didn't buy.
              const lifetimeApplies = hasContinuedAttribution(row);

              return (
                <tr
                  key={row.id}
                  className={`border-b border-[#dfe0e1] last:border-0 hover:bg-[#fafafa] ${rowHeightClass}`}
                >
                  {/* Clip rather than wrap, so a long store name doesn't make
                      its row twice as tall as its neighbours. */}
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[180px] truncate underline"
                      title={row.storeName}
                    >
                      {row.storeName}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[140px] truncate"
                      title={row.storeType}
                    >
                      {row.storeType}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[140px] truncate"
                      title={row.location}
                    >
                      {row.location}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333]">
                    {AD_TYPE_LABEL[row.customerType]}
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333]">
                    {formatCustomerDate(row.adClickDate)}
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333] underline">
                    {formatCustomerDate(order.date)}
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 text-[#333] ${figureAlignClass}`}
                  >
                    {formatCustomerCurrency(order.amount)}
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 ${figureAlignClass}`}
                  >
                    {lifetimeApplies ? (
                      <LifetimeCell
                        value={
                          row.lifetimeSales != null
                            ? formatCustomerCurrency(row.lifetimeSales)
                            : null
                        }
                      />
                    ) : (
                      <NotApplicableCell />
                    )}
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 ${figureAlignClass}`}
                  >
                    {lifetimeApplies ? (
                      <LifetimeCell
                        underline
                        value={
                          row.lifetimeOrders != null
                            ? String(row.lifetimeOrders)
                            : null
                        }
                      />
                    ) : (
                      <NotApplicableCell />
                    )}
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-10 text-center text-sm text-[#757575]"
                >
                  {COPY.customers.table.empty}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex justify-center">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClick={setPage}
          />
        </div>
      ) : null}
    </div>
  );
}
